import clsx from 'clsx';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { goBack } from '../lib/router';

/**
 * Градивните елементи на интерфейса. Едно правило: всичко, което се натиска,
 * е поне 64 px високо и има надпис с думи, не с икона.
 */

export function Screen({
  title,
  subtitle,
  back,
  logo,
  children,
  footer,
}: {
  title: string;
  subtitle?: string | null;
  back?: boolean;
  /** Логото вляво — на главните екрани, не на вътрешните. */
  logo?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <header className="safe-top sticky top-0 z-20 bg-brand text-white shadow-[0_2px_0_0_var(--app-accent)]">
        <div className="flex min-h-16 items-center gap-3 px-3 py-2">
          {back ? (
            <button
              type="button"
              onClick={goBack}
              aria-label="Назад"
              className="tap -ml-1 flex w-14 items-center justify-center rounded-xl text-accent active:bg-white/10"
            >
              <ChevronLeft size={36} strokeWidth={2.5} />
            </button>
          ) : null}
          {logo ? <Logo /> : null}
          <div className="min-w-0 flex-1">
            {/* По-дългите заглавия се пренасят на втори ред: с логото отляво
                „Непредвиден разход" иначе се реже по средата на думата. */}
            <h1 className="line-clamp-2 text-2xl font-bold leading-tight">{title}</h1>
            {subtitle ? <div className="truncate text-base text-white/70">{subtitle}</div> : null}
          </div>
        </div>
      </header>
      <main className="flex-1 px-3 pb-6 pt-3">{children}</main>
      {footer ? (
        <footer className="safe-bottom sticky bottom-0 z-20 border-t-2 border-line bg-paper px-3 py-3">
          {footer}
        </footer>
      ) : null}
    </div>
  );
}

type Tone = 'go' | 'brand' | 'stop' | 'plain' | 'accent';

const TONES: Record<Tone, string> = {
  /** Следващата стъпка: зеленото „X" от логото с черен текст. */
  accent: 'bg-accent text-accent-ink border-accent shadow-[0_6px_0_0_rgba(0,0,0,0.18)]',
  go: 'bg-go text-white border-go',
  brand: 'bg-brand text-[color:var(--app-brand-ink)] border-brand',
  stop: 'bg-stop text-white border-stop',
  plain: 'bg-card text-ink border-line',
};

export function BigButton({
  children,
  onClick,
  tone = 'brand',
  disabled,
  type = 'button',
  icon,
  hint,
}: {
  children: ReactNode;
  onClick?: () => void;
  tone?: Tone;
  disabled?: boolean;
  type?: 'button' | 'submit';
  icon?: ReactNode;
  hint?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'no-select flex w-full flex-col items-center justify-center gap-1 rounded-2xl border-2 px-4 py-5 text-center text-2xl font-bold active:scale-[0.99] disabled:opacity-40',
        TONES[tone],
      )}
      style={{ minHeight: 88 }}
    >
      <span className="flex items-center gap-3">
        {icon}
        {children}
      </span>
      {hint ? <span className="text-base font-normal opacity-90">{hint}</span> : null}
    </button>
  );
}

export function LinkButton({
  children,
  href,
  tone = 'plain',
  icon,
}: {
  children: ReactNode;
  href: string;
  tone?: Tone;
  icon?: ReactNode;
}) {
  return (
    <a
      href={href}
      className={clsx(
        'tap flex w-full items-center justify-center gap-3 rounded-2xl border-2 px-4 py-4 text-xl font-bold',
        TONES[tone],
      )}
    >
      {icon}
      {children}
    </a>
  );
}

export function Card({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const classes = clsx('rounded-2xl border-2 border-line bg-card p-4', className);
  if (!onClick) return <div className={classes}>{children}</div>;
  return (
    <button type="button" onClick={onClick} className={clsx(classes, 'w-full text-left active:opacity-80')}>
      {children}
    </button>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return <div className="text-base font-semibold uppercase tracking-wide text-muted">{children}</div>;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  autoFocus,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
  autoFocus?: boolean;
  maxLength?: number;
}) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode ?? 'text'}
        autoFocus={autoFocus}
        maxLength={maxLength}
        className="mt-1 w-full rounded-xl border-2 border-line bg-paper px-4 py-4 text-2xl"
      />
    </label>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        /* Сървърът приема до 1000 знака — по-дълго би спряло цялата опашка. */
        maxLength={1000}
        className="mt-1 w-full rounded-xl border-2 border-line bg-paper px-4 py-3 text-xl"
      />
    </label>
  );
}

export function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      className="tap flex w-full items-center justify-between gap-4 rounded-2xl border-2 border-line bg-card px-4 py-4 text-left"
    >
      <span className="flex-1">
        <span className="block text-xl font-semibold">{label}</span>
        {description ? <span className="block text-base text-muted">{description}</span> : null}
      </span>
      <span
        className={clsx(
          'flex h-10 w-18 shrink-0 items-center rounded-full border-2 px-1 transition-colors',
          checked ? 'justify-end border-go bg-go' : 'justify-start border-line bg-paper',
        )}
        style={{ width: 72 }}
      >
        <span className="block h-8 w-8 rounded-full bg-paper shadow" />
      </span>
    </button>
  );
}

export function Choice<T extends string>({
  options,
  value,
  onChange,
  columns = 2,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  columns?: number;
}) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={clsx(
            'tap rounded-2xl border-2 px-3 py-3 text-lg font-semibold',
            option.value === value ? 'border-go bg-go text-white' : 'border-line bg-card text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Banner({
  tone = 'plain',
  children,
}: {
  tone?: 'plain' | 'warn' | 'stop' | 'go';
  children: ReactNode;
}) {
  const tones: Record<string, string> = {
    plain: 'border-line bg-card',
    warn: 'border-warn bg-warn/10 text-warn',
    stop: 'border-stop bg-stop/10 text-stop',
    go: 'border-go bg-go/10 text-go',
  };
  return (
    <div className={clsx('rounded-2xl border-2 p-3 text-lg font-semibold', tones[tone])}>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-10 text-center text-xl text-muted">{children}</p>;
}

/**
 * Логото на КМД ЕКСПРЕС за горната лента — истинското, изрязано от оригинала
 * (scripts/brand/build-logos.py), във варианта за тъмен фон.
 */
export function Logo() {
  return (
    <img
      src="/brand/kmdx-mark-dark.webp"
      alt="KMDX EXPRESS — КМД ЕКСПРЕС"
      className="h-9 w-auto max-w-none shrink-0 select-none"
      draggable={false}
      decoding="async"
    />
  );
}
